import React, { useState } from 'react';
import Button from './Button';
import { Badge } from './Badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './Card';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from './Dialog';
import { cn } from '../lib/utils';
import { 
  Check, 
  Sparkles, 
  CreditCard, 
  Shield, 
  Info,
  Loader2,
  Zap,
  Star,
  Crown
} from 'lucide-react';

interface CreditPack {
  id: string;
  credits: number;
  price: string;
  priceId: string;
  popular?: boolean;
  icon: React.ReactNode;
  savings?: string;
}

interface CreditPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseCredits: (priceId: string) => Promise<void>;
}

const CREDIT_PACKS: CreditPack[] = [
  {
    id: 'starter',
    credits: 5,
    price: '$5.00 AUD',
    priceId: 'price_1SVnLQ8ZtYxRZ3sb0Mow5mEI',
    icon: <Zap className="h-6 w-6" />,
  },
  {
    id: 'popular',
    credits: 25,
    price: '$10.00 AUD',
    priceId: 'price_1SVnLs8ZtYxRZ3sbWV7c4vp2',
    popular: true,
    icon: <Star className="h-6 w-6" />,
    savings: 'Save 60%',
  },
  {
    id: 'value',
    credits: 200,
    price: '$49.00 AUD',
    priceId: 'price_1SVnN28ZtYxRZ3sbrSrx9hi5',
    icon: <Crown className="h-6 w-6" />,
    savings: 'Save 75%',
  },
];

const features = [
  { text: 'essay markings', perCredit: true },
  { text: 'AI feedback & analysis' },
  { text: 'No expiry date' },
];

const CreditPurchaseModal: React.FC<CreditPurchaseModalProps> = ({
  isOpen,
  onClose,
  onPurchaseCredits,
}) => {
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);

  const handlePurchase = async (priceId: string) => {
    setLoadingPriceId(priceId);
    try {
      await onPurchaseCredits(priceId);
    } catch (error) {
      setLoadingPriceId(null);
    }
  };

  const calculatePerCredit = (price: string, credits: number) => {
    const numericPrice = parseFloat(price.replace(/[^\d.]/g, ''));
    return (numericPrice / credits).toFixed(2);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto p-6 sm:p-8">
        <DialogHeader className="text-center pb-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Sparkles className="h-7 w-7 text-primary" />
          </div>
          <DialogTitle className="text-2xl sm:text-3xl">Choose a Credit Pack</DialogTitle>
          <DialogDescription className="text-base sm:text-lg mt-2">
            Purchase credits to mark essays and get AI feedback
          </DialogDescription>
        </DialogHeader>

        {/* Credit Packs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-8">
          {CREDIT_PACKS.map((pack) => {
            const isLoading = loadingPriceId === pack.priceId;
            const isDisabled = loadingPriceId !== null;
            
            return (
              <Card
                key={pack.id}
                className={cn(
                  "relative overflow-hidden transition-all duration-200 cursor-pointer group",
                  pack.popular 
                    ? "border-2 border-primary shadow-lg ring-1 ring-primary/20" 
                    : "border hover:border-primary/50 hover:shadow-md",
                  isDisabled && !isLoading && "opacity-60"
                )}
                onClick={() => !isDisabled && handlePurchase(pack.priceId)}
              >
                {/* Popular Badge */}
                {pack.popular && (
                  <div className="absolute -top-px left-0 right-0 flex justify-center">
                    <Badge className="rounded-t-none rounded-b-lg px-4 py-1 bg-primary text-primary-foreground">
                      Most Popular
                    </Badge>
                  </div>
                )}

                {/* Savings Badge */}
                {pack.savings && (
                  <div className="absolute top-3 right-3">
                    <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200">
                      {pack.savings}
                    </Badge>
                  </div>
                )}

                <CardContent className={cn("p-6 text-center", pack.popular && "pt-10")}>
                  {/* Icon */}
                  <div className={cn(
                    "mx-auto w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-colors",
                    pack.popular 
                      ? "bg-primary/10 text-primary" 
                      : "bg-secondary text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                  )}>
                    {pack.icon}
                  </div>

                  {/* Credits */}
                  <div className="mb-4">
                    <p className="text-4xl font-bold text-foreground">{pack.credits}</p>
                    <p className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                      Credits
                    </p>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    <p className={cn(
                      "text-3xl font-bold",
                      pack.popular ? "text-primary" : "text-foreground"
                    )}>
                      {pack.price}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      ~${calculatePerCredit(pack.price, pack.credits)} AUD per credit
                    </p>
                  </div>

                  {/* Features */}
                  <div className="space-y-2 mb-6">
                    {features.map((feature, idx) => (
                      <div key={idx} className="flex items-center justify-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-muted-foreground">
                          {feature.perCredit ? `${pack.credits} ${feature.text}` : feature.text}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Purchase Button */}
                  <Button
                    variant={pack.popular ? "default" : "outline"}
                    className={cn(
                      "w-full",
                      pack.popular && "shadow-md"
                    )}
                    disabled={isDisabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePurchase(pack.priceId);
                    }}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Purchase
                      </>
                    )}
                  </Button>
                </CardContent>

                {/* Hover Effect Gradient */}
                {pack.popular && (
                  <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
                )}
              </Card>
            );
          })}
        </div>

        {/* Info Section */}
        <Card className="bg-secondary/50 border-secondary">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Info className="h-4 w-4 text-primary" />
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-foreground">How Credits Work</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Each essay marking consumes 1 credit
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Re-marking an essay uses 1 credit
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    AI feedback analysis uses 1 credit
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Credits never expire
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground pt-1 border-t border-border">
                  <Shield className="h-4 w-4 text-green-500" />
                  Secure payment processed by Stripe
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <DialogFooter className="sm:justify-center pt-2">
          <Button variant="ghost" onClick={onClose} className="px-8">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreditPurchaseModal;
